import { isNative } from "../voice/index.js";

interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener(type: "release", cb: () => void): void;
}

/**
 * Keep the screen on (SPEC 8): Screen Wake Lock on the web, keep-awake plugin on Android.
 * Night dimming lowers the brightness on Android; on the web the scene itself gets darker.
 */
export class Awake {
  private lock: WakeLockSentinelLike | null = null;
  private enabled = false;

  async enable(): Promise<void> {
    this.enabled = true;
    document.addEventListener("visibilitychange", this.reacquire);
    await this.acquire();
  }

  async disable(): Promise<void> {
    this.enabled = false;
    document.removeEventListener("visibilitychange", this.reacquire);
    await this.lock?.release().catch(() => {});
    this.lock = null;
    if (isNative()) {
      const { KeepAwake } = await import("@capacitor-community/keep-awake");
      await KeepAwake.allowSleep().catch(() => {});
    }
  }

  private reacquire = () => {
    if (this.enabled && !document.hidden) void this.acquire();
  };

  private async acquire() {
    if (isNative()) {
      const { KeepAwake } = await import("@capacitor-community/keep-awake");
      await KeepAwake.keepAwake().catch(() => {});
      return;
    }
    const nav = navigator as Navigator & {
      wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> };
    };
    if (!nav.wakeLock || this.lock) return;
    try {
      this.lock = await nav.wakeLock.request("screen");
      this.lock.addEventListener("release", () => (this.lock = null));
    } catch {
      this.lock = null;
    }
  }

  /** Night: minimum brightness on Android (-1 restores the system setting). */
  async setDim(dim: boolean): Promise<void> {
    if (!isNative()) return;
    const { ScreenBrightness } = await import("@capacitor-community/screen-brightness");
    await ScreenBrightness.setBrightness({ brightness: dim ? 0.02 : -1 }).catch(() => {});
  }
}
